import { X } from 'lucide-static';
import { Icon } from './Icon';
import { delayMs, lateinit } from 'shared/util';

type ToastProps = {
	icon?: string;
	title: string;
	actions?: { name: string; action: () => void }[];
	closable?: boolean;
};
export function Toast({ icon, title, actions, closable }: ToastProps) {
	const view = (
		<div class="flex w-fit items-center gap-4 rounded-md border border-gray-200 bg-white px-4 py-2 shadow-md">
			{icon && <Icon icon={icon} class="size-5 text-gray-700" />}
			<span class="text-gray-800">{title}</span>
			{(actions ?? []).map(({ name, action }) => (
				<button class="rounded-md px-2 py-1 text-sm font-bold hover:bg-gray-100" on:click={action}>
					{name}
				</button>
			))}
			{closable && (
				<button class="rounded-full p-1 opacity-95 hover:bg-gray-100" on:click={() => view.remove()}>
					<Icon icon={X} class="size-3" />
				</button>
			)}
		</div>
	);
	return view;
}

type ContentToastProps = {
	icon?: string;
	title: string;
	content: string | HTMLElement;
	actions?: { name: string; action: () => void }[];
	closable?: boolean;
};
export function ContentToast({ title, actions, closable, content }: ContentToastProps) {
	const view = (
		<div class="flex w-fit flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-md">
			<div class="flex items-start justify-between">
				<span class="font-bold text-gray-800">{title}</span>
				{closable && (
					<button class="rounded-full p-1 opacity-95 hover:bg-gray-100" on:click={() => view.remove()}>
						<Icon icon={X} class="size-4" />
					</button>
				)}
			</div>
			{content}
			<div class="flex justify-end gap-4">
				{(actions ?? []).map(({ name, action }) => (
					<button class="rounded-md bg-gray-50 px-2 py-1" on:click={action}>
						{name}
					</button>
				))}
			</div>
		</div>
	);
	return view;
}

let toastContainer: HTMLElement = lateinit;

export function showToast(toast: HTMLElement, duration: number = -1) {
	if (!toastContainer) {
		toastContainer = <div class="pointer-events-none absolute inset-0 grid grid-cols-1 content-end justify-items-end gap-3 p-4"></div>;
		document.body.append(toastContainer);
	}

	const toastWrap = (
		<div style="pointer-events: all;" class="origin-bottom">
			{toast}
		</div>
	);

	toastContainer.append(toastWrap);
	const intrinsicHeight = `${toast.scrollHeight}px`; // Get intrinsic height

	toastWrap.animate(
		[
			{ height: '0', opacity: '.6' },
			{ height: intrinsicHeight, opacity: '1' },
		],
		{
			duration: 300,
			easing: 'cubic-bezier(.16,.04,0,.96)',
			fill: 'forwards', // Ensure it stays at final state
		},
	);

	if (duration > -1) {
		delayMs(duration).then(() => {
			toastWrap
				.animate(
					[
						{ opacity: '1', scale: '1', translate: '0 0rem' },
						{ opacity: '0', scale: '0.8', translate: '0 3rem' },
					],
					{
						duration: 200,
						easing: 'cubic-bezier(.86,.02,.96,.68)',
					},
				)
				.finished.then(() => toastWrap.remove());
		});
	}
}
